import { useEffect, useRef } from "react";
import {
  addRequest,
  updateRequest,
  removeRequest,
  fetchRequestsByEntityID,
} from "../../state/slices/requestmonitor";
import useHubContext from "../useHubContext";
import { useDispatch } from "react-redux";
import { PARKING_ACTIVITY_STATUS } from "../../constants";

const useRequestMonitorListener = ({ entityID, facilityGroupID }) => {
  const dispatch = useDispatch();
  const { portalHub, Connected: PortalHubConnected } = useHubContext();
  const facilityGroupIDRef = useRef(facilityGroupID); // Store facilityGroupID persistently
  const entityIDRef = useRef(entityID); // Store entityID persistently

  useEffect(() => {
    facilityGroupIDRef.current = facilityGroupID; // Update ref when facilityGroupID changes
  }, [facilityGroupID]);

  useEffect(() => {
    entityIDRef.current = entityID; // Update ref when entityID changes
  }, [entityID]);

  useEffect(() => {
    if (!PortalHubConnected) return;

    portalHub.subscribe(
      "VALET_TICKET_STATUS_CHANGED",
      handleValetTicketStatusChange
    );

    return () => {
      portalHub.unsubscribe("VALET_TICKET_STATUS_CHANGED");
    };
  }, [PortalHubConnected]);

  const getEntityRequestMonitorData = () => {
    dispatch(fetchRequestsByEntityID(entityIDRef.current));
  };

  const mapTicketDetails = (ticketDetails) => {
    return {
      credentialID: ticketDetails.CredentialID,
      ticketNumber: ticketDetails.TicketNumber,
      requestedDate: ticketDetails.RequestedDate,
      firstName: ticketDetails.FirstName,
      lastName: ticketDetails.LastName,
      licensePlate: ticketDetails.LicensePlate,
      parkedSpace: ticketDetails.ParkedSpace,
      retrievedDate: ticketDetails.RetrievedDate,
      retrievedBy: ticketDetails.RetrievedBy,
      entityID: ticketDetails.EntityID,
      entityName: ticketDetails.EntityName,
      parentEntityID: ticketDetails.ParentEntityID,
    };
  };

  const processNotification = (status, updatedTicketDetails) => {
    // Handle the status and dispatch the appropriate action
    switch (status) {
      case PARKING_ACTIVITY_STATUS.VALET_REQUEST:
        dispatch(addRequest(updatedTicketDetails));
        break;
      case PARKING_ACTIVITY_STATUS.VALET_DELIVERY:
        dispatch(removeRequest(updatedTicketDetails));
        break;
      case PARKING_ACTIVITY_STATUS.VALET_RETRIEVAL:
        dispatch(updateRequest(updatedTicketDetails));
        break;
      default:
        console.warn("Unhandled Status:", status);
        break;
    }
  };

  const handleValetTicketStatusChange = (request) => {
    try {
      const requestData = JSON.parse(request);
      const messageData = JSON.parse(requestData.Message);

      const { Status, TicketDetails } = messageData;
      const updatedTicketDetails = mapTicketDetails(TicketDetails);

      //Processes notification only when one of the below criteria matches:
      //Standalone Valet -- ContextEntityid should match the notification entityid
      //Valet Under FG - FacilityGroupID should match notification entity's parententityid
      if (facilityGroupIDRef.current) {
        if (updatedTicketDetails?.parentEntityID === facilityGroupIDRef.current) {
          processNotification(Status, updatedTicketDetails);
        }
      } else if (updatedTicketDetails?.entityID === entityIDRef.current) {
        processNotification(Status, updatedTicketDetails);
      }
    } catch (error) {
      console.error(
        "Error handling VALET_TICKET_STATUS_CHANGED message:",
        error
      );
    }
  };

  return {
    getEntityRequestMonitorData,
    handleValetTicketStatusChange,
  };
};

export default useRequestMonitorListener;
